import { useCallback, useEffect, useState } from 'react';
import { Calculator, RefreshCw } from 'lucide-react';
import { api } from '../api/client';
import type { QuotaPolicySummary, QuotaRunResponse } from '../api/client';
import { fmt } from '@cca/ui';
import { fmtMoney } from '../lib/format';
import { computeDiocesanFyTotals, type QuotaBinding } from '../lib/quotaBinding';

type Props = {
  onClose: () => void;
  baseYear: number;
  /** Diocese CPI Y1 default — compounds the run total into Y2/Y3. */
  cpiY1: number;
  /** GL 210 (Diocesan Quota) — the fixed budget line the run total flows
   *  into. currentAmount is the diocese's existing annual quota figure,
   *  used as the prior-CY component of the Y1 split. */
  boundLine: { code: string; name: string; currentAmount: number } | null;
  /** The persisted binding, if any. */
  binding: QuotaBinding | null;
  /** Persist a binding + push its FY totals into row 210's quotaY* slots.
   *  The parent no-ops when nothing changed, so re-binding is idempotent. */
  onBind: (binding: QuotaBinding, fyTotals: { y1: number; y2: number; y3: number }) => void;
  /** Drop the binding (row 210 keeps its last-written values). */
  onClear: () => void;
};

/** The budget's Quota drawer (AdminPanel pattern — slides over the budget,
 *  which stays visible and live-updates as bindings land): bind a saved
 *  Quota model policy version so GL 210's forecast autopopulates from the
 *  real schedule. Replaces the retired QuotaCalculatorLegacy — policy
 *  content (exclusions, bands, caps) is authored in the Quota workspace,
 *  not here. */
export function QuotaPolicyPanel({
  onClose,
  baseYear,
  cpiY1,
  boundLine,
  binding,
  onBind,
  onClear,
}: Props) {
  const [policies, setPolicies] = useState<QuotaPolicySummary[]>([]);
  const [policiesLoaded, setPoliciesLoaded] = useState(false);
  const [run, setRun] = useState<QuotaRunResponse | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current210 = boundLine?.currentAmount ?? 0;

  const runAndBind = useCallback(
    (policyVersion: string, assessmentYear: number) => {
      setRunning(true);
      setError(null);
      api
        .quotaRun(policyVersion, assessmentYear)
        .then((r) => {
          setRun(r);
          onBind(
            { policyVersion, assessmentYear },
            computeDiocesanFyTotals(r.total_payable, current210, cpiY1),
          );
        })
        .catch((e: Error) => setError(e.message))
        .finally(() => setRunning(false));
    },
    [onBind, current210, cpiY1],
  );

  useEffect(() => {
    api
      .quotaPolicies()
      .then((list) => setPolicies(list.filter((p) => !p.archived)))
      .catch((e: Error) => setError(e.message))
      .finally(() => setPoliciesLoaded(true));
  }, []);

  // Refresh the bound run's schedule on entry so the view (and row 210)
  // reflects the latest curated data, not last visit's numbers.
  // Run-on-entry only — deliberately not re-triggered by later binding
  // changes (runAndBind handles those directly).
  useEffect(() => {
    if (binding) runAndBind(binding.policyVersion, binding.assessmentYear);
  }, []);

  const selectPolicy = (policyVersion: string) => {
    if (!policyVersion) return;
    const policy = policies.find((p) => p.policy_version === policyVersion);
    const year = binding?.assessmentYear ?? policy?.effective_assessment_year ?? baseYear + 1;
    runAndBind(policyVersion, year);
  };

  const selectYear = (assessmentYear: number) => {
    if (!binding || !Number.isFinite(assessmentYear)) return;
    runAndBind(binding.policyVersion, assessmentYear);
  };

  const fyTotals = run ? computeDiocesanFyTotals(run.total_payable, current210, cpiY1) : null;
  const boundPolicy = binding
    ? policies.find((p) => p.policy_version === binding.policyVersion)
    : undefined;
  const computable = run ? run.results.filter((r) => r.computable) : [];
  const skipped = run ? run.results.filter((r) => !r.computable) : [];

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- backdrop click-to-close duplicates the explicit Close button inside
    <div className="fixed inset-0 z-40 bg-black/30 no-print" onClick={onClose}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- click handler only stops backdrop-close propagation, no action of its own */}
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-[640px] bg-paper-cream shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-paper-cream border-b border-paper-divider px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="w-5 h-5 text-ccaanz-gold" />
            <h2 className="text-[16px] font-medium text-ccaanz-black">Diocesan Quota</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ccaanz-grey hover:text-ccaanz-black text-[14px]"
            aria-label="Close"
          >
            Close
          </button>
        </div>

        <div className="p-6">
          {/* ── Controls ── */}
          <section className="panel">
            <div className="panel-section">
              <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4">
                <div className="flex flex-wrap items-start gap-x-6 gap-y-4">
                  <div>
                    <div className="panel-section-title">Quota policy</div>
                    <select
                      value={binding?.policyVersion ?? ''}
                      onChange={(e) => selectPolicy(e.target.value)}
                      className="input mt-3 text-[13px] min-w-56"
                      disabled={running}
                    >
                      <option value="" disabled>
                        {policiesLoaded ? 'Choose a saved policy…' : 'Loading policies…'}
                      </option>
                      {policies.map((p) => (
                        <option key={p.policy_version} value={p.policy_version}>
                          {p.label ? `${p.label} (${p.policy_version})` : p.policy_version}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <div className="panel-section-title">Invoice year</div>
                    <input
                      type="number"
                      step={1}
                      value={binding?.assessmentYear ?? baseYear + 1}
                      onChange={(e) => selectYear(parseInt(e.target.value, 10))}
                      disabled={!binding || running}
                      className="input num mt-3 w-24 text-right text-[13px]"
                    />
                  </div>
                </div>
                {binding && (
                  <div className="flex flex-wrap gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => runAndBind(binding.policyVersion, binding.assessmentYear)}
                      className="btn btn-ghost btn-sm"
                      disabled={running}
                      title="Re-run the bound policy against the latest curated data"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                    <button
                      type="button"
                      onClick={onClear}
                      className="btn btn-ghost btn-sm"
                      disabled={running}
                      title="Remove the binding — GL 210 keeps its last-written forecast"
                    >
                      Unbind
                    </button>
                  </div>
                )}
              </div>
              <p className="text-[11px] text-ccaanz-grey mt-4 pt-3 border-t border-paper-divider">
                The selected policy runs against the curated assessment base and its total flows
                into GL 210 (Diocesan Quota): Y1 is locked to the result; Y2/Y3 default to it (CPI{' '}
                {cpiY1}% compounded) but stay editable. Policy content — exclusions, bands, caps —
                is authored on the Quota tab, not here.
              </p>
            </div>
          </section>

          {error && (
            <div className="panel mt-3">
              <div className="panel-section">
                <div className="text-[14px] font-medium text-ccaanz-red mb-1">Quota run failed</div>
                <div className="text-ccaanz-grey text-[12px]">{error}</div>
              </div>
            </div>
          )}

          {!error && !binding && policiesLoaded && (
            <div className="panel mt-3">
              <div className="panel-section text-[13px] text-ccaanz-grey">
                {policies.length === 0
                  ? 'No saved quota policies yet. Create one on the Quota tab (Policy & Run), then bind it here.'
                  : 'Choose a policy above to autopopulate the budget from its quota schedule.'}
              </div>
            </div>
          )}

          {binding && !boundLine && (
            <div className="panel mt-3">
              <div className="panel-section text-[13px] text-ccaanz-red">
                GL code 210 (Diocesan Quota) isn't in the budget — the schedule total has nowhere to
                flow. Sync from Xero from the Admin panel.
              </div>
            </div>
          )}

          {/* ── Summary strip ── */}
          {run && fyTotals && binding && (
            <section className="panel mt-3 mb-3 no-print">
              <div className="panel-section flex flex-wrap items-baseline justify-between gap-4">
                <div className="flex gap-6 text-[12px]">
                  {(
                    [
                      [`Into 210 FY${baseYear + 1}`, fyTotals.y1],
                      [`FY${baseYear + 2}`, fyTotals.y2],
                      [`FY${baseYear + 3}`, fyTotals.y3],
                    ] as const
                  ).map(([label, value]) => (
                    <div key={label} className="text-right">
                      <div className="text-[10px] uppercase tracking-[0.08em] text-ccaanz-grey">
                        {label}
                      </div>
                      <div className="num text-[15px] font-medium">{fmtMoney(value)}</div>
                    </div>
                  ))}
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-ccaanz-grey">
                    Total quota CY{run.assessment_year}
                  </div>
                  <div className="num text-[18px] font-semibold text-ccaanz-blue mt-0.5">
                    {fmtMoney(run.total_payable)}
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ── Schedule ── */}
          {run && binding && (
            <section className="mt-4 mb-6">
              <div className="flex items-baseline justify-between flex-wrap gap-2 pb-2 border-b border-edge">
                <h2 className="text-[15px] font-semibold text-ccaanz-gold tracking-[0.005em]">
                  Quota schedule
                </h2>
                <div className="text-xs text-ccaanz-grey">
                  {boundPolicy?.label ?? binding.policyVersion} · CY{run.assessment_year} ·{' '}
                  {computable.length} of {run.count} churches computable
                </div>
              </div>
              <table className="budget-table mt-4">
                <colgroup>
                  <col />
                  <col className="w-32 col-actual" />
                  <col className="w-28 col-current" />
                  <col className="w-28 col-shade" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Church</th>
                    <th className="right col-actual">Smoothed assessable</th>
                    <th className="right col-current">Payable quota</th>
                    <th className="right col-shade">Adjustments</th>
                  </tr>
                </thead>
                <tbody>
                  {computable.map((r) => {
                    const adjustments = [
                      r.cap_applied && 'cap',
                      r.yoy_cap_applied && 'YoY cap',
                      r.floor_applied && 'floor',
                      r.collar_applied && 'collar',
                      r.phase_in_factor < 1 && 'phase-in',
                    ].filter(Boolean);
                    return (
                      <tr key={r.church_id} className="row-data">
                        <td>
                          <span className="line-name">{r.name}</span>
                        </td>
                        <td className="col-actual text-right num">{fmt(r.smoothed_assessable)}</td>
                        <td className="col-current text-right num">{fmt(r.payable_quota)}</td>
                        <td className="col-shade text-right text-[11px] text-ccaanz-grey">
                          {adjustments.length > 0 ? adjustments.join(', ') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="row-total">
                    <td>Total</td>
                    <td className="col-actual text-right num">
                      {fmt(computable.reduce((s, r) => s + r.smoothed_assessable, 0))}
                    </td>
                    <td className="col-current text-right num">{fmt(run.total_payable)}</td>
                    <td className="col-shade" />
                  </tr>
                </tbody>
              </table>
              {skipped.length > 0 && (
                <p className="text-[11px] text-ccaanz-grey mt-3">
                  Not computable (insufficient assessment data):{' '}
                  {skipped.map((r) => r.name).join(', ')} — curate their years on the Quota tab.
                </p>
              )}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
